import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Eye, Settings2 } from "lucide-react";
import { useView } from "../lib/viewContext";
import { ScopeOption } from "./ScopeOption";
import {
  loadScopeDefinitions,
  primaryTitle,
  type Primary,
  type ScopeDefinitions,
  FALLBACK_SCOPE_DEFS,
} from "../lib/scope-meta";

/** Hierarchical scope-view selector. Primary row (All / Personal /
 *  Work / Private) plus a contextual secondary strip when Personal
 *  or Work is active. The selection is the new {primary, sub} shape;
 *  it is mapped onto the existing server-persisted ViewDef list (by
 *  scope-name match) so filtering keeps working until the backend
 *  half exposes native primary/sub view semantics. No regression:
 *  every pick still drives setActiveView with a real viewId. */
const PRIMARIES: (Primary | "all")[] = ["all", "personal", "work", "private"];

export function ViewSelector() {
  const { views, activeView, activeViewId, setActiveView, loading } =
    useView();
  const [open, setOpen] = useState(false);
  const [primary, setPrimary] = useState<Primary | "all">("all");
  const [sub, setSub] = useState<string | null>(null);
  const [defs, setDefs] = useState<ScopeDefinitions>(FALLBACK_SCOPE_DEFS);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void loadScopeDefinitions().then(setDefs);
  }, []);

  // Reflect server state: derive primary from the active view scopes.
  useEffect(() => {
    if (!activeView) return;
    if (activeView.isEverything) {
      setPrimary("all");
      setSub(null);
      return;
    }
    const s = activeView.scopes ?? [];
    if (s.includes("private")) setPrimary("private");
    else if (s.includes("work")) setPrimary("work");
    else setPrimary("personal");
  }, [activeView]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        popRef.current &&
        !popRef.current.contains(t) &&
        triggerRef.current &&
        !triggerRef.current.contains(t)
      )
        setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    const tm = setTimeout(
      () => document.addEventListener("mousedown", onDocClick),
      0,
    );
    document.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(tm);
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const subs = useMemo<string[]>(() => {
    if (primary === "personal") return ["all", ...defs.personal];
    if (primary === "work") return ["all", ...defs.work];
    return [];
  }, [primary, defs]);

  const label = useMemo(() => {
    if (primary === "all") return "All";
    const p = primaryTitle(primary);
    if (!sub || sub === "all" || sub === "general") return p;
    return `${p} · ${sub.charAt(0).toUpperCase()}${sub.slice(1)}`;
  }, [primary, sub]);

  /** Map a {primary, sub} choice onto the closest existing ViewDef
   *  and persist it (keeps the live filter working pre-backend). */
  function applyMapping(p: Primary | "all", s: string | null) {
    if (loading && views.length === 0) return;
    let target = activeViewId;
    if (p === "all") {
      target =
        views.find((v) => v.isEverything)?.viewId ??
        views[0]?.viewId ??
        null;
    } else {
      const want = s && s !== "all" && s !== "general" ? s : p;
      const byScope =
        views.find((v) => !v.isEverything && v.scopes?.includes(want)) ??
        views.find((v) => !v.isEverything && v.scopes?.includes(p)) ??
        views.find((v) =>
          v.name
            .toLowerCase()
            .includes((s && s !== "all" ? s : p) as string),
        );
      target = byScope?.viewId ?? target;
    }
    if (target && target !== activeViewId) void setActiveView(target);
  }

  function pickPrimary(p: Primary | "all") {
    setPrimary(p);
    setSub(null);
    applyMapping(p, null);
    if (p === "all" || p === "private") setOpen(false);
  }
  function pickSub(s: string) {
    setSub(s === "all" ? null : s);
    applyMapping(primary, s);
    setOpen(false);
  }

  return (
    <div className="fixed top-2 right-2 z-50 md:top-3 md:right-4">
      <div className="relative">
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="menu"
          aria-expanded={open}
          title={`View: ${label}`}
          className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 dark:border-slate-700 bg-white/85 dark:bg-slate-800/85 backdrop-blur px-3 py-1.5 text-sm font-medium text-slate-700 dark:text-slate-200 shadow-sm hover:bg-white dark:hover:bg-slate-800 transition-colors"
        >
          <Eye size={14} className="opacity-70 shrink-0" />
          <span className="max-w-[8rem] md:max-w-[14rem] truncate">
            {label}
          </span>
          <ChevronDown size={14} className="opacity-60 shrink-0" />
        </button>

        {open && (
          <div
            ref={popRef}
            role="menu"
            aria-label="Select scope view"
            tabIndex={-1}
            className="absolute right-0 mt-2 w-[260px] max-w-[calc(100vw-1rem)] max-h-[calc(100vh-5rem)] overflow-y-auto rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-xl shadow-slate-300/40 dark:shadow-black/50 animate-popoverIn motion-reduce:animate-none"
          >
            <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500 border-b border-slate-100 dark:border-slate-700/60">
              Scope
            </div>
            <div
              role="radiogroup"
              aria-label="Scope"
              className="flex flex-col gap-1.5 p-2"
            >
              {PRIMARIES.map((p) => (
                <ScopeOption
                  key={p}
                  compact
                  selected={primary === p}
                  onSelect={() => pickPrimary(p)}
                  label={p === "all" ? "All" : primaryTitle(p)}
                />
              ))}
            </div>
            {subs.length > 0 && (
              <div
                role="radiogroup"
                aria-label={`${primary} sub-category`}
                className="flex flex-col gap-1.5 px-2 pb-2 border-t border-slate-100 dark:border-slate-700/60 pt-2 animate-popoverIn motion-reduce:animate-none"
              >
                {subs.map((s) => {
                  const activeS =
                    (sub ?? "all") === s || (sub === null && s === "all");
                  const text =
                    s === "all"
                      ? `All ${primary}`
                      : s.charAt(0).toUpperCase() + s.slice(1);
                  return (
                    <ScopeOption
                      key={s}
                      compact
                      selected={activeS}
                      onSelect={() => pickSub(s)}
                      label={text}
                    />
                  );
                })}
              </div>
            )}
            {primary === "private" && (
              <p className="px-3 pb-2 text-[11px] text-slate-400 dark:text-slate-500">
                Private has no sub-categories. Memso auto-detects
                private content; re-scope from a packet's menu.
              </p>
            )}
            <Link
              to="/settings/views"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-3 py-2.5 text-sm text-slate-600 dark:text-slate-300 border-t border-slate-100 dark:border-slate-700/60 hover:bg-slate-50 dark:hover:bg-slate-700/40"
            >
              <Settings2 size={14} className="opacity-70" />
              Manage views…
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
